/**
 * OKR Alignment Map Page
 * Interactive visual map showing OKR relationships using ReactFlow
 */

import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import ReactFlow, {
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  addEdge,
  Connection,
  MiniMap,
  NodeMouseHandler,
  useReactFlow,
  ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/common/Button';
import { ArrowLeft, Maximize2, Download, AlertCircle, Filter, Minimize2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import { nodeTypes } from '../../components/okr/nodes';
import { useAllOKRs } from '../../hooks/useOKRs';
import { useAuth } from '../../context/AuthContext';
import { buildOKRGraph } from '../../utils/okrGraphBuilder';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { OKRNodeTooltip } from '../../components/okr/OKRNodeTooltip';
import { AlignmentMapFilters, AlignmentFilters } from '../../components/okr/AlignmentMapFilters';
import { LayoutControls, LayoutOptions } from '../../components/okr/LayoutControls';
import { ViewToggles } from '../../components/okr/ViewToggles';
import { OKRDetailSidebar } from '../../components/okr/OKRDetailSidebar';
import { AlignmentMapLegend } from '../../components/okr/AlignmentMapLegend';
import { OKR } from '../../types/okr';
import { useUrlState } from '../../hooks/useUrlState';
import { useOpenWithReturn } from '../../hooks/useReturnNavigation';
import {
  ALIGNMENT_URL_DEFAULTS,
  ALIGNMENT_URL_ALLOWED,
  alignmentFiltersToUrl,
  layoutOptionsToUrl,
  urlToAlignmentFilters,
  urlToLayoutOptions,
} from '../../utils/alignmentMapUrlState';

// Inner component that uses ReactFlow hooks
const AlignmentMapContent: React.FC = () => {
  const navigate = useNavigate();
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const reactFlowRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Load every OKR the caller may read, for every role. The backend applies the privacy
  // rule (buildOkrPrivacyWhere): company/department/team OKRs for everyone, Individual OKRs
  // per the tenant setting and the reporting line. This used to send `ownerIds: [me]` for
  // employees and viewers, so their parents never loaded and no alignment line was drawn
  // (a viewer, who owns nothing, got an empty map). All pages: a parent on page 2 is
  // otherwise missing too.
  const { data: okrListResponse, isLoading: okrsLoading, error } = useAllOKRs(undefined, {
    enabled: !!user?.id,
  });

  // Consider loading if user not loaded OR OKRs still loading
  const isLoading = !user?.id || okrsLoading;

  // ReactFlow state
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Tooltip state
  const [hoveredNode, setHoveredNode] = useState<{ okr: OKR; position: { x: number; y: number } } | null>(null);

  // Filters, layout and the open sidebar live in the URL (utils/alignmentMapUrlState.ts):
  // in component state, "View full details" and then Back reset the whole map.
  const [view, setView] = useUrlState(ALIGNMENT_URL_DEFAULTS, ALIGNMENT_URL_ALLOWED);
  const openWithReturn = useOpenWithReturn();

  // Selected node for the detail sidebar, by id so it survives the round trip
  const selectedOKR = useMemo(
    () => (view.okr ? okrListResponse?.okrs.find((o) => o.id === view.okr) ?? null : null),
    [view.okr, okrListResponse]
  );
  const setSelectedOKR = useCallback(
    (okr: OKR | null) => setView({ okr: okr?.id ?? '' }),
    [setView]
  );

  // Compute current quarter dates so the alignment map opens scoped to the
  // current quarter rather than the full historical hierarchy.
  const currentQuarterInfo = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const quarter = Math.floor(now.getMonth() / 3) + 1;
    const startMonth = (quarter - 1) * 3;
    const startDate = new Date(year, startMonth, 1);
    const endDate = new Date(year, startMonth + 3, 0, 23, 59, 59, 999);
    return { year, quarter, startDate, endDate, label: `Q${quarter} ${year}` };
  }, []);

  // Filters state
  const [filtersOpen, setFiltersOpen] = useState(false);
  // Keyed on the filter fields only: opening the sidebar (`okr`) or changing the layout must
  // not rebuild the filtered node set.
  const { types, statuses, q, pmin, pmax, depts, period, from, to } = view;
  const filters: AlignmentFilters = useMemo(
    () => urlToAlignmentFilters({ ...ALIGNMENT_URL_DEFAULTS, types, statuses, q, pmin, pmax, depts, period, from, to }, currentQuarterInfo),
    [types, statuses, q, pmin, pmax, depts, period, from, to, currentQuarterInfo]
  );
  const setFilters = useCallback(
    (next: AlignmentFilters) => setView(alignmentFiltersToUrl(next)),
    [setView]
  );

  // Layout options
  const { dir, nodesp, ranksp, minimap } = view;
  const layoutOptions: LayoutOptions = useMemo(
    () => urlToLayoutOptions({ ...ALIGNMENT_URL_DEFAULTS, dir, nodesp, ranksp, minimap }),
    [dir, nodesp, ranksp, minimap]
  );
  const setLayoutOptions = useCallback(
    (next: LayoutOptions) => setView(layoutOptionsToUrl(next)),
    [setView]
  );

  // Export and fullscreen state
  const [isExporting, setIsExporting] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Filter OKRs based on current filters
  const filterOKRs = useCallback((okrs: OKR[]): OKR[] => {
    return okrs.filter((okr) => {
      // Type filter
      if (!filters.types.has(okr.type)) return false;

      // Status filter
      if (filters.statuses.size > 0 && !filters.statuses.has(okr.status)) return false;

      // Search filter
      if (filters.search && !okr.title.toLowerCase().includes(filters.search.toLowerCase())) {
        return false;
      }

      // Progress filter
      const progress = okr.progress || 0;
      if (progress < filters.progressMin || progress > filters.progressMax) return false;

      // Department filter
      if (filters.departments.size > 0) {
        const ownerDept = okr.owner.department || '';
        if (!filters.departments.has(ownerDept)) return false;
      }

      // Time period filter - check if OKR overlaps with selected period
      if (filters.timePeriod.startDate && filters.timePeriod.endDate) {
        const okrStart = okr.startDate ? new Date(okr.startDate) : null;
        const okrEnd = okr.endDate ? new Date(okr.endDate) : null;

        if (okrStart && okrEnd) {
          // Check if date ranges overlap
          // OKR overlaps if: OKR.start <= filter.end AND OKR.end >= filter.start
          const overlaps = okrStart <= filters.timePeriod.endDate && okrEnd >= filters.timePeriod.startDate;
          if (!overlaps) return false;
        }
      }

      return true;
    });
  }, [filters]);

  // Handle view toggle
  const handleViewToggle = useCallback((type: string) => {
    const newTypes = new Set(filters.types);
    if (newTypes.has(type)) {
      newTypes.delete(type);
    } else {
      newTypes.add(type);
    }
    setFilters({ ...filters, types: newTypes });
  }, [filters, setFilters]);

  // Handle export to PNG
  const handleExport = useCallback(async () => {
    if (!reactFlowRef.current) return;

    setIsExporting(true);
    try {
      const dataUrl = await toPng(reactFlowRef.current, {
        backgroundColor: '#f9fafb',
        filter: (node) => {
          // Exclude certain elements from export (like controls, minimap)
          if (
            node?.classList?.contains('react-flow__controls') ||
            node?.classList?.contains('react-flow__minimap') ||
            node?.classList?.contains('react-flow__attribution')
          ) {
            return false;
          }
          return true;
        },
      });

      // Create download link
      const link = document.createElement('a');
      const date = new Date().toISOString().split('T')[0];
      link.download = `okr-alignment-map-${date}.png`;
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Error exporting alignment map:', error);
      alert('Failed to export alignment map. Please try again.');
    } finally {
      setIsExporting(false);
    }
  }, []);

  // Handle fullscreen toggle
  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.error('Error attempting to enable fullscreen:', err);
        alert('Failed to enter fullscreen mode.');
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  // Monitor fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  // Build graph when OKRs are loaded, filters change, or layout options change
  useEffect(() => {
    if (okrListResponse && okrListResponse.okrs.length > 0) {
      const filteredOKRs = filterOKRs(okrListResponse.okrs);
      const { nodes: graphNodes, edges: graphEdges } = buildOKRGraph(filteredOKRs, {
        direction: layoutOptions.direction,
        nodeSpacing: layoutOptions.nodeSpacing,
        rankSpacing: layoutOptions.rankSpacing,
      });
      setNodes(graphNodes);
      setEdges(graphEdges);
    }
  }, [okrListResponse, filters, layoutOptions, filterOKRs, setNodes, setEdges]);

  // Update node and edge selection state
  useEffect(() => {
    if (selectedOKR) {
      const selectedNodeId = `okr-${selectedOKR.id}`;

      // Find all connected node IDs
      const connectedNodeIds = new Set<string>();
      edges.forEach((edge) => {
        if (edge.source === selectedNodeId) {
          connectedNodeIds.add(edge.target);
        }
        if (edge.target === selectedNodeId) {
          connectedNodeIds.add(edge.source);
        }
      });

      // Update nodes with selection and connection state
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          data: {
            ...node.data,
            selected: node.data.okr?.id === selectedOKR.id,
            connected: connectedNodeIds.has(node.id),
          },
          style: {
            ...node.style,
            opacity: node.data.okr?.id === selectedOKR.id || connectedNodeIds.has(node.id) ? 1 : 0.4,
          },
        }))
      );

      // Update edges - highlight connected edges
      setEdges((eds) =>
        eds.map((edge) => {
          const isConnected = edge.source === selectedNodeId || edge.target === selectedNodeId;

          if (isConnected) {
            // Determine edge color based on source node type
            let edgeColor = '#3b82f6'; // Default blue
            const sourceNode = nodes.find(n => n.id === edge.source);
            if (sourceNode?.type === 'company') edgeColor = '#3b82f6'; // Blue
            else if (sourceNode?.type === 'department') edgeColor = '#f59e0b'; // Amber
            else if (sourceNode?.type === 'team') edgeColor = '#10b981'; // Green
            else if (sourceNode?.type === 'individual') edgeColor = '#a855f7'; // Purple

            return {
              ...edge,
              animated: true,
              style: {
                stroke: edgeColor,
                strokeWidth: 3,
                opacity: 1,
              },
            };
          }

          // Dim non-connected edges
          return {
            ...edge,
            animated: false,
            style: {
              stroke: '#d1d5db',
              strokeWidth: 1,
              opacity: 0.2,
            },
          };
        })
      );
    } else {
      // Reset nodes
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          data: {
            ...node.data,
            selected: false,
            connected: false,
          },
          style: {
            ...node.style,
            opacity: 1,
          },
        }))
      );

      // Reset edges to default style
      setEdges((eds) =>
        eds.map((edge) => ({
          ...edge,
          animated: false,
          style: {
            stroke: '#94a3b8',
            strokeWidth: 2,
            opacity: 0.6,
          },
        }))
      );
    }
  }, [selectedOKR, nodes, edges, setNodes, setEdges]);

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  // Handle node hover
  const onNodeMouseEnter: NodeMouseHandler = useCallback((event, node) => {
    const okr = node.data.okr as OKR;
    if (okr) {
      // Get mouse position relative to viewport
      const rect = event.currentTarget.getBoundingClientRect();
      setHoveredNode({
        okr,
        position: {
          x: rect.right,
          y: rect.top + rect.height / 2,
        },
      });
    }
  }, []);

  const onNodeMouseLeave: NodeMouseHandler = useCallback(() => {
    setHoveredNode(null);
  }, []);

  // Handle node click
  const onNodeClick: NodeMouseHandler = useCallback((_event, node) => {
    const okr = node.data.okr as OKR;
    if (okr) {
      setSelectedOKR(okr);
      // Close tooltip when sidebar opens
      setHoveredNode(null);
    }
  }, [setSelectedOKR]);

  // Get available departments
  const availableDepartments = React.useMemo(() => {
    if (!okrListResponse) return [];
    const depts = new Set<string>();
    okrListResponse.okrs.forEach((okr) => {
      if (okr.owner.department) {
        depts.add(okr.owner.department);
      }
    });
    return Array.from(depts).sort();
  }, [okrListResponse]);

  // Calculate visible OKRs count
  const visibleOKRsCount = React.useMemo(() => {
    if (!okrListResponse) return 0;
    return filterOKRs(okrListResponse.okrs).length;
  }, [okrListResponse, filterOKRs]);

  return (
    <div className="h-screen flex flex-col bg-cream">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-stone-200">
        <div className="max-w-full px-6 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/okrs')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to OKRs
              </Button>
              <div>
                <h1 className="text-2xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>
                  OKR Alignment Map
                </h1>
                <p className="text-sm text-stone-500 mt-1">
                  Visualize how OKRs connect across your organization
                  {filters.timePeriod.preset !== 'all' && (
                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 text-xs font-medium">
                      Viewing: {filters.timePeriod.preset === 'current-quarter'
                        ? currentQuarterInfo.label
                        : filters.timePeriod.preset === 'current-year'
                          ? `${new Date().getFullYear()}`
                          : 'Custom period'}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant={filtersOpen ? 'primary' : 'outline'}
                size="sm"
                onClick={() => setFiltersOpen(!filtersOpen)}
                className="flex items-center gap-2"
              >
                <Filter className="w-4 h-4" />
                {filtersOpen ? 'Hide' : 'Show'} Filters
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                disabled={isExporting}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                {isExporting ? 'Exporting...' : 'Export'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleFullscreen}
                className="flex items-center gap-2"
              >
                {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              </Button>
            </div>
          </div>

          {/* View Toggles */}
          <div className="flex items-center justify-center">
            <ViewToggles
              activeTypes={filters.types}
              onToggle={handleViewToggle}
            />
          </div>
        </div>
      </div>

      {/* ReactFlow Canvas */}
      <div ref={reactFlowRef} className="flex-1 relative">
        {/* Filters Sidebar */}
        <AlignmentMapFilters
          filters={filters}
          onFiltersChange={setFilters}
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          totalOKRs={okrListResponse?.okrs.length || 0}
          visibleOKRs={visibleOKRsCount}
          availableDepartments={availableDepartments}
        />

        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-cream z-10">
            <LoadingSpinner size="lg" text="Loading OKR alignment map..." />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-cream z-10">
            <div className="text-center max-w-md">
              <AlertCircle className="w-12 h-12 text-danger-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-stone-900 mb-2">
                Failed to Load Alignment Map
              </h3>
              <p className="text-stone-500 mb-4">
                {error instanceof Error ? error.message : 'An error occurred while loading OKRs'}
              </p>
              <Button onClick={() => window.location.reload()}>
                Retry
              </Button>
            </div>
          </div>
        )}

        {/* Empty State — also fires when current-period filter excludes all OKRs */}
        {!isLoading && !error && okrListResponse && visibleOKRsCount === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-cream z-10">
            <div className="text-center max-w-md">
              <div className="w-16 h-16 bg-stone-200 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-stone-400" />
              </div>
              <h3 className="text-lg font-semibold text-stone-900 mb-2">
                {filters.timePeriod.preset === 'current-quarter'
                  ? `No OKRs for ${currentQuarterInfo.label}`
                  : 'No OKRs Found'}
              </h3>
              <p className="text-stone-500 mb-4">
                {filters.timePeriod.preset !== 'all'
                  ? 'No alignment to display for this period.'
                  : 'Create your first OKR to see the alignment map'}
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Button onClick={() => openWithReturn('/okrs/new')}>
                  {filters.timePeriod.preset === 'current-quarter' ? 'Start alignment for this quarter' : 'Create OKR'}
                </Button>
                {filters.timePeriod.preset !== 'all' && (
                  <Button
                    variant="ghost"
                    onClick={() =>
                      setFilters({
                        ...filters,
                        timePeriod: { startDate: null, endDate: null, preset: 'all' },
                      })
                    }
                  >
                    View all periods
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ReactFlow - Only render when we have data */}
        {!isLoading && !error && okrListResponse && okrListResponse.okrs.length > 0 && (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeMouseEnter={onNodeMouseEnter}
            onNodeMouseLeave={onNodeMouseLeave}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes}
            defaultEdgeOptions={{
              type: 'smoothstep',
              animated: false,
              style: { stroke: '#94a3b8', strokeWidth: 2, opacity: 0.6 },
            }}
            fitView
            attributionPosition="bottom-left"
          >
            <Controls />
            {layoutOptions.showMiniMap && (
              <MiniMap
                nodeColor={(node) => {
                  switch (node.type) {
                    case 'company':
                      return '#3b82f6'; // Company - Blue
                    case 'department':
                      return '#f59e0b'; // Department - Amber
                    case 'team':
                      return '#10b981'; // Team - Green
                    case 'individual':
                      return '#a855f7'; // Individual - Purple
                    default:
                      return '#e5e7eb';
                  }
                }}
                maskColor="rgb(240, 240, 240, 0.6)"
              />
            )}
            <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
          </ReactFlow>
        )}

        {/* Tooltip */}
        {hoveredNode && (
          <OKRNodeTooltip okr={hoveredNode.okr} position={hoveredNode.position} />
        )}

        {/* Layout Controls */}
        {!isLoading && !error && okrListResponse && okrListResponse.okrs.length > 0 && (
          <LayoutControls
            options={layoutOptions}
            onOptionsChange={setLayoutOptions}
            onFitView={() => fitView({ duration: 300 })}
            onZoomIn={() => zoomIn({ duration: 300 })}
            onZoomOut={() => zoomOut({ duration: 300 })}
          />
        )}

        {/* Detail Sidebar */}
        <OKRDetailSidebar
          okr={selectedOKR}
          isOpen={selectedOKR !== null}
          onClose={() => setSelectedOKR(null)}
        />

        {/* Legend */}
        {!isLoading && !error && okrListResponse && okrListResponse.okrs.length > 0 && (
          <AlignmentMapLegend />
        )}
      </div>
    </div>
  );
};

// Outer wrapper component with ReactFlowProvider
export const AlignmentMap: React.FC = () => {
  return (
    <ReactFlowProvider>
      <AlignmentMapContent />
    </ReactFlowProvider>
  );
};
